import React, { useContext } from "react";
import Navbar from "components/navigation/web/Navbar";
import { useTranslation } from "react-i18next";
import {
	Box,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	IconButton,
	Grid,
	Menu,
	MenuItem,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { getBorder } from "utils/Styles";
import { gettingSubPage } from "utils/Timekeeper";
import { useNavigate, useSearchParams, useLocation, useParams } from "react-router-dom";
import { GlobalContext } from "GlobalContext";
/* ICONS */
import Groups2Icon from "@mui/icons-material/Groups2";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { jwtDecode } from "jwt-decode";
import { ContentBreadcrumbs } from 'components/navigation/web/ContentBreadcrumbs'
import { buildSubpagePath, getSubpageFromLocation } from 'utils/subpageRouting'

// Define drawer width constants
const drawerWidth = 240;
const openedMixin = (theme) => ({
	width: drawerWidth,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: "hidden",
	backgroundColor: theme.palette.background.paper,
});

const closedMixin = (theme) => ({
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: "hidden",
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up("sm")]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
	backgroundColor: theme.palette.background.paper,
});

// Usa un componente Box estilizado en lugar de un Drawer
const SideMenu = styled(Box, { shouldForwardProp: (prop) => prop !== "open" })(
	({ theme, open }) => ({
		height: `calc(100vh - 64px)`,
		borderRight: getBorder(theme.palette.mode),
		whiteSpace: "nowrap",
		boxSizing: "border-box",
		display: "flex",
		flexDirection: "column",
		...(open && {
			...openedMixin(theme),
			backgroundColor: theme.palette.background.paper,
		}),
		...(!open && {
			...closedMixin(theme),
			backgroundColor: theme.palette.background.paper,
		}),
	})
);

const StableTooltip = styled(({ className, ...props }) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	fontSize: "0.85rem", // Tamaño de texto más legible
	color: theme.palette.common.white,
	maxWidth: "auto", // Ancho máximo aumentado
	wordWrap: "break-word", // Permite romper palabras largas
	whiteSpace: "normal", // Permite múltiples líneas
}));

const gettingAllowViews = () => {
	if (localStorage.getItem("access_token")) {
		return jwtDecode(localStorage.getItem("access_token"))?.views;
	} else {
		return [];
	}
};

const useMenuItems = (showResume = true) => {
	const { t } = useTranslation();

	const menuItems = [
		{
			fullpath: "/timekeeper?subpage=resume",
			routePath: "/timekeeper/resume",
			path: "resume",
			icon: <AutoGraphIcon />,
			label: t("timekeeper.title"),
			order: 1,
		},
		{
			fullpath: "/timekeeper?subpage=timecard",
			routePath: "/timekeeper/timecard",
			path: "timecard",
			icon: <AlarmOnIcon />,
			label: t("timekeeper.submenu.timecard"),
		},
		{
			fullpath: "/timekeeper?subpage=employees",
			routePath: "/timekeeper/employees",
			path: "employees",
			icon: <Groups2Icon />,
			label: t("timekeeper.submenu.employees"),
		},
		{
			fullpath: "/timekeeper?subpage=scheduler",
			routePath: "/timekeeper/scheduler",
			path: "scheduler",
			icon: <CalendarMonthIcon />,
			label: t("timekeeper.submenu.schedule"),
		},
	];

	const visibleMenuItems = showResume ? menuItems : menuItems.filter((item) => item.path !== "resume");

	const allowedViews = visibleMenuItems.filter((item) =>
		gettingAllowViews().includes(item.fullpath)
	);

	const sortedMenuItems = allowedViews.sort((a, b) => {
		const orderA = a.order !== undefined ? a.order : Infinity;
		const orderB = b.order !== undefined ? b.order : Infinity;
		return orderA - orderB || a.label.localeCompare(b.label);
	});

	return sortedMenuItems;
};

const Component = () => {
	const theme = useTheme();
	const { hide, openLeft } = useContext(GlobalContext);
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = React.useState(() => {
		const stored = localStorage.getItem("web_submenu_open");
		return stored === null ? !hide : stored === "true";
	});

	const navigate = useNavigate();
	const location = useLocation();

	const menuItems = useMenuItems(!openLeft);
	const { subpage: paramsSubpage } = useParams();
	const [searchParams] = useSearchParams();
	const selectedSubpage = getSubpageFromLocation(paramsSubpage, searchParams)
	const selectedItem = menuItems.find((item) => item.path === selectedSubpage)

	const toggleDrawer = () => {
		const nextOpen = !isOpen;
		localStorage.setItem("web_submenu_open", String(nextOpen));
		setIsOpen(nextOpen);
	};

	const quickSwitchMenus = [
		{ path: "/settings/resume", label: t("settings.title"), match: "/settings" },
		{ path: "/timekeeper/resume", label: t("timekeeper.title"), match: "/timekeeper" },
		{ path: "/user_management/resume", label: t("user_management.title"), match: "/user_management" },
		{ path: "/custom_reports/resume", label: t("custom_reports.title"), match: "/custom_reports" },
		{ path: "/accruals/resume", label: t("accruals.title"), match: "/accruals" },
		{ path: "/schedule-rules/resume", label: t("schedule_rules.title"), match: "/schedule-rules" },
	];

	const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);

	const handleOpenQuickSwitch = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
	};

	const handleHoverOpenQuickSwitch = (event) => {
		if (isOpen) return;
		setMenuAnchorEl(event.currentTarget);
	};

	const handleCloseQuickSwitch = () => {
		setMenuAnchorEl(null);
	};

	const handleQuickSwitch = (path) => {
		handleCloseQuickSwitch();
		navigate(path);
	};

	return (
		<Box
			display={"flex"}
			sx={{
				padding: 0,
				margin: 0,
			}}
		>
			<SideMenu open={isOpen} sx={{ minWidth: isOpen ? 240 : 64 }}>
				<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
					<List sx={{ flexGrow: 1, overflowY: "auto" }}>
						{menuItems.map((item) => (
							<StableTooltip
								key={item.path}
								enterDelay={700}
								arrow
								disableInteractive
								title={!isOpen ? (item.path === "resume" ? "" : item.label) : ""}
								placement="right"
							>
								<ListItemButton
									sx={{
										minHeight: 49,
										height: "auto",
										px: 2.5,
										justifyContent: isOpen
											? "initial"
											: "center",
										alignItems: "center",
									}}
									selected={
										selectedSubpage === item.path
									}
									onClick={() => navigate(item.routePath || buildSubpagePath('/timekeeper', item.path))}
									onMouseEnter={item.path === "resume" ? handleHoverOpenQuickSwitch : undefined}
								>
									<ListItemIcon
										sx={{
											minWidth: 0,
											mr: isOpen ? 3 : "auto",
											justifyContent: "center",
											alignItems: "center",
											height: 24,
										}}
									>
										{item.icon}
									</ListItemIcon>
									<ListItemText
										primary={item.label}
										sx={{
											opacity: isOpen ? 1 : 0,
											padding: 0,
										}}
									/>
									{item.path === "resume" && (
										<IconButton
											size="small"
											onClick={handleOpenQuickSwitch}
											sx={{ ml: "auto", display: isOpen ? "inline-flex" : "none" }}
										>
											<ChevronRightIcon fontSize="small" />
										</IconButton>
									)}
								</ListItemButton>
							</StableTooltip>
						))}
					</List>
					<Menu
						anchorEl={menuAnchorEl}
						open={Boolean(menuAnchorEl)}
						onClose={handleCloseQuickSwitch}
						anchorOrigin={{ vertical: "top", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "left" }}
						MenuListProps={{ onMouseLeave: handleCloseQuickSwitch }}
					>
						{quickSwitchMenus.map((menu) => (
							<MenuItem key={menu.path} onClick={() => handleQuickSwitch(menu.path)}>
								{menu.label}
							</MenuItem>
						))}
					</Menu>
					<Box sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, display: "flex", justifyContent: isOpen ? "flex-end" : "center", p: 1 }}>
						<IconButton onClick={toggleDrawer}>
							{isOpen ? <LastPageIcon /> : <FirstPageIcon />}
						</IconButton>
					</Box>
				</Box>
			</SideMenu>
			<Grid
				container
				sx={{
					flexGrow: 1,
					px: 2,
					pt: 1,
					pb: 1,
					transition: theme.transitions.create("margin", {
						easing: theme.transitions.easing.sharp,
						duration: theme.transitions.duration.leavingScreen,
					}),
					...(isOpen && {
						transition: theme.transitions.create("margin", {
							easing: theme.transitions.easing.easeOut,
							duration: theme.transitions.duration.enteringScreen,
						}),
					}),
				}}
			>
				<Grid
					size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
					sx={{
						flexGrow: 1,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
						overflowX: "hidden",
						overflowY: "hidden",
					}}
				>
					{selectedItem && selectedItem.path !== 'resume' && (
						<ContentBreadcrumbs
							items={[
								{ label: t('home.title'), to: '/' },
								{ label: selectedItem.label }
							]}
						/>
					)}
					{gettingSubPage(
						selectedSubpage,
						false,
						location
					)}
				</Grid>
			</Grid>
		</Box>
	);
};

const Timekeeper = () => {
	const { t } = useTranslation();
	const { openLeft } = useContext(GlobalContext);
	const { subpage: paramsSubpage } = useParams();
	const [searchParams] = useSearchParams();
	const menuItems = useMenuItems(!openLeft);
	const selectedSubpage = getSubpageFromLocation(paramsSubpage, searchParams)

	return (
		<>
			<Navbar
				view={{
					title:
						t("timekeeper.title") +
						" - " +
						menuItems.find(
							(item) => item.path === selectedSubpage
						)?.label,
					component: <Component />,
				}}
				right={{
					title: "Right",
					component: <> componente </>,
				}}
			/>
		</>
	);
};

export { Timekeeper, useMenuItems };
